import React, { useRef, useContext } from 'react';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeContext, ThemeOverride, ThemeOptions, } from '@wix/da-react-context/pkg/ThemeContext';
import { MatureContext } from '@wix/da-react-context/pkg/MatureContext';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData } from '@wix/da-bi/pkg/events';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useCurrentPromo } from '../../../utils/hooks/useCurrentPromo';
import TierBadge from '@wix/da-ds/pkg/Badges/TierBadge';
import StandardMenu from '../../../Dropdown/MenuPreset/StandardMenu';
import UserLink from '../../../User/Link';
import SaleBanner from '../../../SaleBanner';
import LogoutItem from '../../_partials/LogoutItem';
import CoreMenuItem from '../../_partials/CoreMenuItem';
import VerificationReminder from '../../_partials/VerificationReminder';
import UserMenuAvatar from './UserMenuAvatar';
import { getMatureToggle, getThemeToggle } from './menuItemHelpers';
import userMenuStyles from './UserMenu.scss';
function formatNumber(number) {
    return number > 999 ? shortenNumber(number, 0, Math.floor) : number;
}
var LoggedInUserMenu = function (_a) {
    var _b, _c;
    var currentUser = _a.currentUser, canEnableMatureContent = _a.canEnableMatureContent, isUserMenuOpened = _a.isUserMenuOpened, hasSubscriptions = _a.hasSubscriptions, disableThemeSwitcher = _a.disableThemeSwitcher, toggleTheme = _a.toggleTheme, toggleMature = _a.toggleMature, onToggleUserMenu = _a.onToggleUserMenu, onUserLogoutButtonClicked = _a.onUserLogoutButtonClicked;
    var theme = useContext(ThemeContext);
    var matureContent = useContext(MatureContext);
    var _d = useContext(SessionContext), csrfToken = _d.csrfToken, verificationEmail = _d.verificationEmail, hasCoreMembership = _d.hasCoreMembership, coreLevel = _d.coreLevel, counts = _d.counts;
    var currentPromo = useCurrentPromo('self');
    var t = useTranslation().t;
    var logoutItemRef = useRef(null);
    var handleLogout = function () {
        onUserLogoutButtonClicked();
        // Avoid a race condition and let sagas do their thing before submitting the form
        setTimeout(function () {
            logoutItemRef.current && logoutItemRef.current.submit();
        }, 100);
    };
    var tabIndex = isUserMenuOpened ? 0 : -1;
    var personalGroupItems = [
        {
            label: (React.createElement("div", { className: userMenuStyles['two-side-label'] },
                React.createElement("span", null, t('siteHeaderMobile.userMenu.notes')),
                !!(counts === null || counts === void 0 ? void 0 : counts.notes) && (React.createElement("span", { className: userMenuStyles['menu-item-notification'] }, formatNumber(counts === null || counts === void 0 ? void 0 : counts.notes))))),
            link: Url.buildFromPath(URLS.NOTES),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.NOTES),
                menu_item: 'notes',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
    ];
    var statsGroupItems = [
        {
            label: t('siteHeaderMobile.userMenu.artStore'),
            newTag: true,
            link: URLS.ART_STORE,
            target: '_blank',
            biData: BiData({
                evid: 702,
                link_url: URLS.ART_STORE,
                menu_item: 'artStore',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: (React.createElement("div", { className: userMenuStyles['two-side-label'] },
                React.createElement("span", null, t('siteHeaderMobile.userMenu.pointsBalance')),
                React.createElement("span", null, numeral((_b = counts === null || counts === void 0 ? void 0 : counts.points) !== null && _b !== void 0 ? _b : 0).format('0,0')))),
            link: URLS.POINTS,
            biData: BiData({
                evid: 702,
                link_url: URLS.POINTS,
                menu_item: 'pointsBalance',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: (React.createElement("div", { className: userMenuStyles['two-side-label'] },
                React.createElement("span", null, t('siteHeader.userMenu.fragmentsBalance')),
                React.createElement("span", null, numeral((_c = counts === null || counts === void 0 ? void 0 : counts.fragments) !== null && _c !== void 0 ? _c : 0).format('0,0')))),
            link: 'https://www.deviantartsupport.com/en/article/what-are-badges-and-fragments',
            target: '_blank',
            biData: BiData({
                evid: 702,
                menu_item: 'fragments',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: t('siteHeaderMobile.userMenu.myEarnings'),
            link: URLS.EARNINGS,
            biData: BiData({
                evid: 702,
                link_url: URLS.EARNINGS,
                menu_item: 'myEarnings',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: t('siteHeaderMobile.userMenu.myOrderHistory'),
            link: URLS.ORDERS,
            biData: BiData({
                evid: 702,
                link_url: URLS.ORDERS,
                menu_item: 'order_history',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: t('siteHeaderMobile.userMenu.customerDashboard'),
            link: URLS.CUSTOMER_DASHBOARD,
            biData: BiData({
                evid: 702,
                link_url: URLS.CUSTOMER_DASHBOARD,
                menu_item: 'customer_dashboard',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
    ];
    var lastGroupItems = [
        !disableThemeSwitcher && getThemeToggle(theme, toggleTheme, t),
        canEnableMatureContent && getMatureToggle(!!matureContent, toggleMature, t),
        {
            label: t('siteHeaderMobile.userMenu.settings'),
            link: Url.buildFromPath(URLS.SETTINGS),
            className: userMenuStyles['settings-link'],
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.SETTINGS),
                menu_item: 'settings',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: t('siteHeader.userMenu.helpAndFAQ'),
            link: URLS.HELP,
            biData: BiData({
                evid: 702,
                link_url: URLS.HELP,
                menu_item: 'help',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
        {
            label: (React.createElement(LogoutItem, { formRef: logoutItemRef, token: csrfToken, label: t('siteHeaderMobile.userMenu.logOut') })),
            onClick: handleLogout,
            biData: BiData({
                evid: 702,
                link_url: URLS.LOGOUT,
                menu_item: 'logout',
                menu_name: 'profile',
            }),
            tabIndex: tabIndex,
        },
    ].filter(function (item) { return item; });
    var subscriptionsUrl = Url.userSubscriptionsLink({
        username: currentUser.username,
    });
    var coreLinkUrl = hasCoreMembership
        ? URLS.SETTINGS_BILLING
        : URLS.CORE_MEMBERSHIP;
    return (React.createElement(StandardMenu, { id: "site-header-user-menu", role: "dialog", lightText: true, className: userMenuStyles['user-menu'], itemClassName: userMenuStyles['user-menu-item'], groupClassName: userMenuStyles['user-menu-group'], withPaddings: false, items: [
            {
                className: userMenuStyles['top-group'],
                items: [
                    {
                        label: (React.createElement("div", { className: userMenuStyles['two-side-label'] },
                            React.createElement(UserLink, { user: currentUser, className: userMenuStyles['user-link'], showSymbol: false, tabIndex: tabIndex }),
                            React.createElement(UserMenuAvatar, { currentUser: currentUser, onToggleUserMenu: onToggleUserMenu, isUserMenuOpened: isUserMenuOpened, tabIndex: tabIndex }))),
                    },
                    {
                        label: (React.createElement(CoreMenuItem, { className: userMenuStyles['two-side-label'], hasCoreMembership: hasCoreMembership, coreLevel: coreLevel })),
                        link: coreLinkUrl,
                        biData: BiData({
                            evid: 702,
                            menu_item: hasCoreMembership ? 'my_core' : 'core_membership',
                            menu_name: 'profile',
                            link_url: coreLinkUrl,
                        }),
                        tabIndex: tabIndex,
                    },
                    hasSubscriptions && {
                        label: (React.createElement("div", { className: userMenuStyles['two-side-label'] },
                            React.createElement("span", null, t('siteHeader.userMenu.mySubscriptions')),
                            React.createElement("span", null,
                                React.createElement(ThemeOverride, { theme: ThemeOptions.LIGHT },
                                    React.createElement(TierBadge, { size: 24 }))))),
                        link: subscriptionsUrl,
                        biData: BiData({
                            evid: 702,
                            link_url: subscriptionsUrl,
                            menu_item: 'my_subscriptions',
                            menu_name: 'profile',
                        }),
                        tabIndex: tabIndex,
                    },
                ],
            },
            currentPromo && {
                label: (React.createElement(SaleBanner, { className: userMenuStyles['sale-banner'], isNarrow: true, endDate: currentPromo.endDate, discountValuePercent: currentPromo.discountValuePercent, referralType: ReferralType.PROMOTION_BANNER_USERMENU })),
            },
            !verificationEmail && {
                className: userMenuStyles['group'],
                items: personalGroupItems,
            },
            verificationEmail
                ? {
                    label: React.createElement(VerificationReminder, { email: verificationEmail }),
                    className: userMenuStyles['verification-group'],
                }
                : {
                    className: userMenuStyles['group'],
                    items: statsGroupItems,
                },
            {
                className: userMenuStyles['group'],
                items: lastGroupItems,
            },
        ] }));
};
LoggedInUserMenu.displayName = 'LoggedInUserMenu';
export default LoggedInUserMenu;
//# sourceMappingURL=LoggedInUserMenu.js.map